import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { formatMoney } from 'helpers/helpers'

// Общие куски сетевых отчётов: сравнение филиалов «кто лучше, кто хуже».
// Стили — assets/css/chain-reports.css.

// Палитра серий графиков: филиалы различимы и на светлой, и на тёмной теме
export const CHAIN_COLORS = ['#5b73e8', '#12b886', '#f59f00', '#f06548', '#7c4dff', '#0ab39c', '#e83e8c', '#299cdb', '#8a6d3b', '#6c757d', '#20c997', '#fd7e14']

export function chainColor(index) {
	return CHAIN_COLORS[index % CHAIN_COLORS.length]
}

// Цвета заливки — с прозрачностью, поверх любого фона
const GOOD = '18, 184, 134'
const BAD = '240, 101, 72'
const NEUTRAL = '91, 115, 232'

export function money(value) {
	return formatMoney(Math.round(Number(value || 0)))
}

export function percent(value, digits = 1) {
	if (value === null || value === undefined || isNaN(value)) return '—'
	return `${Number(value).toFixed(digits).replace('.', ',')}%`
}

export function decimal(value, digits = 2) {
	return formatMoney(Number(value || 0), digits)
}

/** min / max колонки по числовому полю (или функции) — для шкал заливки. */
export function columnStats(rows, getter) {
	const read = typeof getter === 'function' ? getter : (row => row[getter])
	let min = null
	let max = null
	rows.forEach(row => {
		const value = read(row)
		if (value === null || value === undefined || value === '' || isNaN(value)) return
		const number = Number(value)
		if (min === null || number < min) min = number
		if (max === null || number > max) max = number
	})
	return { min: min ?? 0, max: max ?? 0 }
}

/**
 * Расходящаяся шкала «худший → лучший»: лучший зелёный, худший красный, середина без цвета.
 * goodHigh = false — для расходов, фудкоста, возвратов: меньше значит лучше.
 */
export function heatStyle(value, stats, goodHigh = true) {
	if (value === null || value === undefined || value === '' || isNaN(value)) return undefined
	if (!stats || stats.max === stats.min) return undefined
	let position = (Number(value) - stats.min) / (stats.max - stats.min)
	if (!goodHigh) position = 1 - position
	const distance = position - 0.5
	const alpha = Math.min(0.34, Math.abs(distance) * 0.68)
	if (alpha < 0.05) return undefined
	return { background: `rgba(${distance > 0 ? GOOD : BAD}, ${alpha.toFixed(3)})` }
}

/**
 * Одноцветная шкала интенсивности: чем больше значение, тем гуще цвет.
 * tone: 'danger' (расходы, списания), 'success' (продажи), 'primary'.
 */
export function intensityStyle(value, max, tone = 'success') {
	const number = Number(value || 0)
	if (!max || number <= 0) return undefined
	const rgb = tone === 'danger' ? BAD : tone === 'primary' ? NEUTRAL : GOOD
	const alpha = 0.06 + Math.min(1, number / max) * 0.42
	return { background: `rgba(${rgb}, ${alpha.toFixed(3)})` }
}

/** Ранги по полю: { pos_id: 1..n }; равные значения — один ранг. */
export function buildRanks(rows, key, goodHigh = true, idKey = 'pos_id') {
	const sorted = rows
		.filter(row => row[key] !== null && row[key] !== undefined && !isNaN(row[key]))
		.sort((a, b) => goodHigh ? Number(b[key]) - Number(a[key]) : Number(a[key]) - Number(b[key]))
	const ranks = {}
	let rank = 0
	let previous = null
	sorted.forEach((row, index) => {
		if (previous === null || Number(row[key]) !== previous) rank = index + 1
		previous = Number(row[key])
		ranks[row[idKey]] = rank
	})
	return { ranks, count: sorted.length }
}

export function RankBadge({ rank, count }) {
	if (!rank) return <span className="chain-rank">—</span>
	let tone = ''
	if (rank === 1) tone = 'gold'
	else if (rank === 2) tone = 'silver'
	else if (rank === 3) tone = 'bronze'
	if (count > 3 && rank === count) tone = 'last'
	return <span className={`chain-rank ${tone}`}>{rank}</span>
}

/**
 * Динамика к прошлому периоду. value — рост в %, null — не с чем сравнить.
 * goodHigh = false — рост это плохо (расходы).
 */
export function Delta({ value, goodHigh = true, showNone = true }) {
	const { t } = useTranslation()
	if (value === null || value === undefined) {
		return showNone ? <span className="chain-delta none" title={t('chain_delta_none_hint')}>{t('chain_delta_new')}</span> : null
	}
	const number = Number(value)
	if (Math.abs(number) < 0.05) return <span className="chain-delta flat">0%</span>
	const good = goodHigh ? number > 0 : number < 0
	return (
		<span className={`chain-delta ${good ? 'up' : 'down'}`}>
			<i className={`uil ${number > 0 ? 'uil-arrow-up' : 'uil-arrow-down'}`}></i>
			{percent(Math.abs(number))}
		</span>
	)
}

/** Ячейка с полосой-заливкой: доля, выполнение плана. */
export function FillCell({ percent: fill, tone = 'primary', className = '', children, onClick, title, style }) {
	const width = Math.max(0, Math.min(100, Number(fill || 0)))
	const rgb = tone === 'danger' ? BAD : tone === 'success' ? GOOD : tone === 'warning' ? '245, 159, 0' : NEUTRAL
	return (
		<td className={`chain-fill chain-num ${onClick ? 'chain-click' : ''} ${className}`} onClick={onClick} title={title} style={style}>
			<div className="chain-fill-bar" style={{ width: `${width}%`, background: `rgba(${rgb}, 0.18)` }}></div>
			<span className="chain-fill-value">{children}</span>
		</td>
	)
}

/** Тон выполнения плана: < 80% красный, < 100% жёлтый, иначе зелёный. */
export function planTone(value) {
	const number = Number(value || 0)
	if (number >= 100) return 'success'
	if (number >= 80) return 'warning'
	return 'danger'
}

/**
 * Сортировка таблицы кликом по заголовку. Ключ — строка; для вычисляемых колонок
 * (ячейка матрицы «pos:58») передайте getValue(row, key).
 */
export function useSort(rows, defaultKey, defaultDir = 'desc', getValue) {
	const [sort, setSort] = useState({ key: defaultKey, dir: defaultDir })

	const sorted = useMemo(() => {
		if (!sort.key) return rows
		const read = row => {
			const value = getValue ? getValue(row, sort.key) : row[sort.key]
			return value === null || value === undefined ? null : value
		}
		const copy = [...rows]
		copy.sort((a, b) => {
			const left = read(a)
			const right = read(b)
			if (left === null && right === null) return 0
			if (left === null) return 1
			if (right === null) return -1
			const result = typeof left === 'string' || typeof right === 'string'
				? String(left).localeCompare(String(right), 'ru')
				: Number(left) - Number(right)
			return sort.dir === 'asc' ? result : -result
		})
		return copy
	}, [rows, sort, getValue])

	function toggle(key) {
		setSort(prev => prev.key === key
			? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
			: { key, dir: key.endsWith('name') ? 'asc' : 'desc' })
	}

	return { sorted, sort, toggle }
}

/** Заголовок колонки с сортировкой. */
export function SortTh({ sortKey, sort, onSort, children, className = '', style, title }) {
	const active = sort.key === sortKey
	const icon = !active ? 'uil-sort' : sort.dir === 'asc' ? 'uil-arrow-up' : 'uil-arrow-down'
	return (
		<th className={`chain-th-sort ${active ? 'active' : ''} ${className}`} style={style} title={title} onClick={() => onSort(sortKey)}>
			{children}
			<i className={`uil ${icon}`}></i>
		</th>
	)
}

/** KPI-карточка над отчётом. */
export function ChainKpi({ title, value, sub, icon, color = '#5b73e8', title2 }) {
	return (
		<div className="card chain-card chain-kpi mb-0">
			<div className="card-body">
				<div className="chain-kpi-head">
					<div className="chain-kpi-title" title={title2}>{title}</div>
					{icon && (
						<div className="chain-kpi-icon" style={{ background: `${color}1f`, color }}>
							<i className={`uil ${icon}`}></i>
						</div>
					)}
				</div>
				<div className="chain-kpi-value" title={typeof value === 'string' ? value : undefined}>{value}</div>
				{sub && <div className="chain-kpi-sub">{sub}</div>}
			</div>
		</div>
	)
}

/** Подпись шкалы цвета под таблицей. */
export function HeatLegend({ mode = 'diverging', low, high }) {
	const { t } = useTranslation()
	const scale = mode === 'danger' ? 'mono-danger' : mode === 'success' ? 'mono-success' : ''
	return (
		<span className="chain-legend">
			<span>{low || (mode === 'diverging' ? t('chain_worst') : t('chain_less'))}</span>
			<span className={`chain-legend-scale ${scale}`}></span>
			<span>{high || (mode === 'diverging' ? t('chain_best') : t('chain_more'))}</span>
		</span>
	)
}

/** Поиск по таблице (локальный, без запроса на сервер). */
export function TableSearch({ value, onChange, placeholder }) {
	const { t } = useTranslation()
	return (
		<div className="chain-search">
			<i className="uil uil-search"></i>
			<input
				type="text"
				className="form-control"
				value={value}
				placeholder={placeholder || t('search')}
				onChange={(e) => onChange(e.target.value)}
			/>
		</div>
	)
}

/** Пустое состояние отчёта. */
export function ChainEmpty({ colSpan, text }) {
	const { t } = useTranslation()
	return (
		<tr>
			<td colSpan={colSpan} className="chain-empty">
				<span className="chain-empty-idle">
					<i className="uil uil-chart-bar d-block mb-1" style={{ fontSize: '22px' }}></i>
					{text || t('chain_no_data')}
				</span>
				<span className="chain-empty-busy">
					<span className="spinner-border spinner-border-sm d-block mx-auto mb-2"></span>
					{t('chain_loading_short')}
				</span>
			</td>
		</tr>
	)
}
